import React, { useEffect, useState } from 'react'
import {
	Button,
	MissionPurpose,
	MissionType,
} from '../../components/StyledComponents'
import { Title } from '../../components/layout/Title'
import { Body } from '../../components/layout/Body'
import { Stepper } from '../../components/features/Stepper'
import Store from '../../store/Store'
import { bookingStepLabels } from './bookingStepLabels'
import {
	ServiceCard,
	ServiceCardGrid,
} from '../../components/StyledComponents'
import { IconCheckBlueRound, IconInfoBlue } from '../../components/Icons'
import { Page } from '../../components/ui/Page'

const CUSTOM_ANALYSIS_SERVICE_ID = 'service-card-8'



const LOCAL_STORAGE_KEY = 'bookingData'

const BookingPage1 = () => {
	// Ensure masterdata is loaded on mount (for refresh)
	useEffect(() => {
		if (!Store.masterdata) {
			(async () => {
				await Store.init();
				forceRender(x => x + 1);
			})();
		}
	}, []);
	const missions = Store.masterdata?.missions || [];
	const presets = Store.masterdata?.presets || [];

	const [, forceRender] = useState(0)
	const [expandedCustomFlow, setExpandedCustomFlow] = useState(
		Store.bookingData?.selectedServiceId === CUSTOM_ANALYSIS_SERVICE_ID
	)
	const [valid, setValid] = useState(false)

	// --- Persist bookingData to localStorage on change
	useEffect(() => {
		if (Store.bookingData) {
			localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(Store.bookingData))
		}
	}, [Store.bookingData])

	// --- Rehydrate bookingData from localStorage on mount
	useEffect(() => {
		if (!Store.bookingData) {
			const saved = localStorage.getItem(LOCAL_STORAGE_KEY)
			if (saved) {
				try {
					Store.setBookingData(JSON.parse(saved))
					forceRender(x => x + 1)
				} catch { }
			}
		}
	}, [])

	const refreshBookingData = (nextBookingData: any) => {
		Store.setBookingData(nextBookingData)
		forceRender(x => x + 1)
	}

	useEffect(() => {
		if (Store.bookingData?.selectedServiceId === CUSTOM_ANALYSIS_SERVICE_ID) {
			setExpandedCustomFlow(true)
		}
	}, [Store.bookingData?.selectedServiceId])

	useEffect(() => {
		if (expandedCustomFlow) {
			onValidate()
		}
	}, [expandedCustomFlow, Store.bookingData?.mission])

	const handleSelectService = (serviceId: string) => {
		// Always reset bookingData to defaults
		const bookingDefaults = {
			id: '',
			name: '',
			selectedServiceId: null,
			mission: 'Landwirtschaft',
			purposes: [],
			coordinates: {},
			dateFrom: null,
			dateTo: null,
			propertyNumber: null,
			fieldCultivation: null,
			area: '',
			information: '',
			price: 10000,
			resultTypes: [],
		}

		let newBookingData = { ...bookingDefaults, selectedServiceId: serviceId }

		if (serviceId === CUSTOM_ANALYSIS_SERVICE_ID) {
			// For custom analysis, select the first mission and its first purpose by default
			const defaultMission = missions[0]
			let purposes = []
			if (defaultMission && defaultMission.purposes && defaultMission.purposes.length > 0) {
				purposes = defaultMission.purposes.map((purpose: any, idx: number) => ({
					...purpose,
					_selected: idx === 0
				}))
			}
			newBookingData = {
				...newBookingData,
				mission: defaultMission ? defaultMission.name : '',
				purposes: purposes,
			}
			refreshBookingData(newBookingData)
			setExpandedCustomFlow(true)
			window.scrollTo({
				top: 0,
				behavior: 'auto',
			})
			return
		}

		// Find the preset and auto-select mission and purposes
		const preset = presets.find((p: any) => `service-card-${p.id}` === serviceId)
		if (preset) {
			// Find the mission that matches preset.mission
			const mission = missions.find((m: any) => m.name === preset.mission)
			let purposes = []
			if (mission && preset.purposes) {
				// Purposes from preset (array of names or ids)
				purposes = (mission.purposes || []).map((purpose: any) => {
					if (preset.purposes.includes(purpose.name) || preset.purposes.includes(purpose.id)) {
						return { ...purpose, _selected: true }
					}
					return { ...purpose, _selected: false }
				})
			} else if (mission) {
				// Select the first purpose by default if no preset purposes
				purposes = (mission.purposes || []).map((purpose: any, idx: number) => ({ ...purpose, _selected: idx === 0 }))
			}

			// Result types from preset
			const resultTypeIds = (preset.resultTypes || []).map((rt: any) => rt.id).filter(Boolean)

			newBookingData = {
				...newBookingData,
				mission: preset.mission || (mission ? mission.name : ''),
				purposes: purposes,
				resultTypes: resultTypeIds,
			}
		}
		refreshBookingData(newBookingData)
		setExpandedCustomFlow(false)
		Store.navigate('booking/page_2')
	}

	const onValidate = () => {
		const currentBookingData = Store.bookingData
		if (!currentBookingData) {
			setValid(false)
			return false
		}

		const selectedMission = missions.find((m: any) => m.name === currentBookingData.mission)

		if (!selectedMission) {
			setValid(false)
			Store.setBookingData({
				...currentBookingData,
				purposes: [],
				resultTypes: [],
			})
			return false
		}

		const selectedResultTypeIds = Array.from(
			new Set(
				(selectedMission.purposes ?? [])
					.filter((p: any) => p._selected)
					.flatMap((p: any) =>
						(p.resultTypes ?? [])
							.filter((r: any) => r._selected)
							.map((r: any) => r.id)
					)
					.filter(Boolean)
			)
		)

		const everySelectedPurposeHasAtLeastOneResultType =
			(selectedMission.purposes ?? []).filter((p: any) => p._selected).length > 0 &&
			(selectedMission.purposes ?? [])
				.filter((p: any) => p._selected)
				.every((p: any) => (p.resultTypes ?? []).some((r: any) => r._selected))

		Store.setBookingData({
			...currentBookingData,
			mission: selectedMission.name,
			purposes: selectedMission.purposes ?? [],
			resultTypes: selectedResultTypeIds,
		})

		setValid(everySelectedPurposeHasAtLeastOneResultType)

		return everySelectedPurposeHasAtLeastOneResultType
	}
	const handleContinueCustomFlow = () => {
		const isValid = onValidate()
		if (!isValid) return

		Store.navigate('booking/page_2')
	}

	const handleBack = () => {
		if (expandedCustomFlow) {
			setExpandedCustomFlow(false)
			refreshBookingData({
				...Store.bookingData,
				selectedServiceId: undefined,
				// mission: undefined, // Do NOT clear mission, keep last selected
				purposes: [],
				resultTypes: [],
			})
			return
		}

		Store.navigate('dashboard')
	}

	return (
		<div className="max-w-[1100px] mx-auto">
			<Page>
				<Body>
					<Stepper numSteps={5} step={1} labels={bookingStepLabels} />

					<Title>Anwendungsfall & Auswertung</Title>

					<div className="max-w-[1040px]">
						{!expandedCustomFlow ? (<p className="text-lg leading-[36px] font-semibold text-[#333]">
							Wählen Sie einen Anwendungsfall.						</p>
						) : (<p className="text-lg leading-[36px] font-semibold text-[#333]">
							Konfigurieren Sie Ihren Drohnenflug.						</p>
						)}
						<p className="mt-6 text-[18px] leading-[30px] text-[#666]">
							Bitte wählen Sie den passenden Anwendungsfall und Auswertung
							für Ihren Flug. Sollten Sie unsicher sein, welcher
							Anwendungsfall für Sie passend ist, so können Sie hier mehr
							Informationen darüber erfahren:{' '}
							<a
								className="text-[#0063A3] underline hover:text-[#004F82] transition-opacity"
								data-pw="link-faq"
								href="/#/faq"
							>
								FAQs
							</a>
						</p>
						{!expandedCustomFlow ? (
							<div className='flex place-items-center pt-10'>
								<IconInfoBlue />
								<p className='text-[#0063A3] ml-2'>Das gewählte Anwendungsfall kann in der Buchungsübersicht jederzeit angepasst werden.</p>

							</div>) : null}
					</div>

					<div className="mt-12 w-full">
						{expandedCustomFlow ? (
							<div className="flex items-center gap-4 mb-6">

								<div className="flex items-center justify-center w-8 h-8 rounded-full text-lg font-semibold">
									<IconCheckBlueRound />
								</div>

								<h2 className="text-lg font-semibold text-[#252525]">
									Anwendungsfall
								</h2>
							</div>
						) : null}

						{!expandedCustomFlow ? (
							<div className="max-w-6xl mx-auto">
								<ServiceCardGrid>
									{presets.map((preset: any, idx: number) => (
										<ServiceCard
											key={preset.id}
											onSelect={handleSelectService}
											id={`service-card-${preset.id}`}
											icon={preset.icon}
											title={preset.name}
											description={preset.description}
											price={preset.price ? `${preset.price} €` : undefined}
											features={preset.resultTypes?.map((rt: any) => rt.description ? `${rt.name} (${rt.description})` : rt.name)}
										/>
									))}
									<ServiceCard
										onSelect={handleSelectService}
										id={CUSTOM_ANALYSIS_SERVICE_ID}
										icon={process.env.PUBLIC_URL + '/assets/card-icons/magicpen.svg'}
										title="Individuelles Analysepaket"
										description="Maßgeschneiderter Drohnenflug für spezielle Anforderungen und individuelle Auswertungen."
										price="150 €"
										isLast={true}
									/>
								</ServiceCardGrid>
							</div>
						) : (
							<div className="ml-3 border-l-2 border-[#DCE9F5] pl-10">
								<div className="rounded-[20px] bg-white">
									<MissionType
										missions={missions}
										bookingData={Store.bookingData ?? {
											id: '',
											selectedServiceId: null,
											name: '',
											mission: '',
											purposes: [],
											coordinates: {},
											dateFrom: null,
											dateTo: null,
											propertyNumber: null,
											fieldCultivation: null,
											area: '',
											information: '',
											price: 0,
											resultTypes: [],
										}}
										onChange={() => {
											forceRender(x => x + 1)
											onValidate()
										}}
									/>
								</div>
							</div>
						)}
					</div>

					{expandedCustomFlow && (
						<div className="mt-16 w-full">
							<div className="flex items-center gap-4 mb-6">
								<div className="flex items-center justify-center w-8 h-8 rounded-full text-lg font-semibold">
									<IconCheckBlueRound />
								</div>
								<h2 className="text-lg font-semibold text-[#252525]">
									Auswertung
								</h2>
							</div>

							<div className="ml-3 border-l-2 border-[#DCE9F5] pl-10">
								<div className="flex items-center gap-3 mb-6 text-[#0063A3]">
									<IconInfoBlue />
									<p className="text-[18px]">
										Es können mehrere Auswertungen gewählt werden.
									</p>
								</div>

								<div className="rounded-[20px] bg-white transition-all duration-300 ease-in-out">
									<MissionPurpose
										content={missions}
										value={Store.bookingData?.mission ?? ''}
										onChange={() => {
											forceRender(x => x + 1)
											onValidate()
										}}
									/>
								</div>
							</div>
						</div>
					)}

					{expandedCustomFlow && (
						<div className="flex w-full justify-end mt-10 transition-all duration-300 ease-in-out">
							<div className="flex flex-row min-w-[380px] gap-4">
								<Button
									className="border border-[#E1320F] text-[#E1320F]"
									data-pw="button-back"
									onClick={handleBack}
								>
									Zurück
								</Button>

								<Button
									data-pw="button-next"
									primary
									enabled={valid}
									onClick={handleContinueCustomFlow}
								>
									Weiter
								</Button>
							</div>
						</div>
					)}
				</Body>
			</Page>
		</div>
	)
}

export default BookingPage1